import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Repeat, Plus, Trash2, Play, Loader2, X, ClipboardCheck, PauseCircle, PlayCircle, CalendarClock, MapPin, UserCheck, Info } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { DE_DATE } from "../lib/dateFormat";

/**
 * Feb 2026 · 6 Sep 2026 · Team-Seite fuer wiederkehrende Aufgaben.
 * User-Directive: „Wiederkehrende Team-Aufgaben (z. B. woechentliche
 * Poolreinigung) mit Frequenz-Auswahl → Cron-basierte Neu-Anlage."
 *
 * Admin darf: anlegen, aendern, aktivieren/pausieren, loeschen, sofort feuern.
 * Concierge darf: eigene sehen + read-only Detail (kein Trigger).
 */

const FREQUENCY_LABEL = {
  daily: "Taeglich",
  weekly: "Woechentlich",
  biweekly: "Zwei-woechentlich",
  monthly: "Monatlich",
};

const WEEKDAYS = [
  { v: 0, label: "Mo" }, { v: 1, label: "Di" }, { v: 2, label: "Mi" },
  { v: 3, label: "Do" }, { v: 4, label: "Fr" }, { v: 5, label: "Sa" }, { v: 6, label: "So" },
];

const CATEGORIES = [
  { v: "home_care", label: "Home Care" },
  { v: "cleaning", label: "Reinigung" },
  { v: "garden", label: "Garten" },
  { v: "winter", label: "Winter" },
  { v: "maintenance", label: "Wartung" },
  { v: "lifestyle", label: "Lifestyle" },
];

const PRIORITIES = [
  { v: "low", label: "Niedrig" },
  { v: "normal", label: "Normal" },
  { v: "high", label: "Hoch" },
  { v: "urgent", label: "Dringend" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return DE_DATE(iso); } catch { return iso; }
}

export default function TeamRecurringTasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null); // Feb 2026 · 17 Sep 2026 · Read-Only-Detail-Modal (Concierges + Admin-Preview)
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p, m] = await Promise.all([
        api.get("/team/recurring-tasks"),
        api.get("/properties"),
        isAdmin ? api.get("/team/members") : Promise.resolve({ data: [] }),
      ]);
      setTasks(t.data || []);
      setProperties(p.data || []);
      setMembers((m.data || []).filter((u) => ["admin", "concierge"].includes(u.role)));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const propNameById = useMemo(
    () => Object.fromEntries(properties.map((p) => [p.id, p.name || p.address || "—"])),
    [properties],
  );
  const propertyById = useMemo(
    () => Object.fromEntries(properties.map((p) => [p.id, p])),
    [properties],
  );
  const memberNameById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.id, m.name || m.email])),
    [members],
  );

  const toggleActive = async (task) => {
    setBusyId(task.id);
    try {
      const { data } = await api.patch(`/team/recurring-tasks/${task.id}`, { active: !task.active });
      setTasks((cur) => cur.map((t) => (t.id === task.id ? data : t)));
      toast.success(data.active ? "Aufgabe aktiviert." : "Aufgabe pausiert.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyId(null); }
  };

  const spawnNow = async (task) => {
    setBusyId(task.id);
    try {
      const { data } = await api.post(`/team/recurring-tasks/${task.id}/spawn-now`);
      setTasks((cur) => cur.map((t) => (t.id === task.id ? data.task : t)));
      toast.success(`Aufgabe gefeuert · neue Sonderwunsch-Zeile #${data.spawned_request_id.slice(0, 8)}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyId(null); }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Aufgabe \u201e${task.title}\u201c wirklich loeschen? Bereits gefeuerte Sonderwuensche bleiben bestehen.`)) return;
    setBusyId(task.id);
    try {
      await api.delete(`/team/recurring-tasks/${task.id}`);
      setTasks((cur) => cur.filter((t) => t.id !== task.id));
      toast.success("Aufgabe geloescht.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyId(null); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="team-recurring-tasks">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Automatisierung</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy flex items-center gap-3">
            <Repeat className="w-8 h-8" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
            Wiederkehrende Aufgaben
          </h1>
          <p className="text-[14px] text-navy-dark/70 mt-3 max-w-2xl leading-relaxed">
            Legen Sie Aufgaben an, die sich in festen Intervallen wiederholen (z. B. woechentliche
            Poolreinigung, monatliche Filter-Wartung). Der Cron feuert jede faellige Aufgabe taeglich
            um 05:15 UTC — sie landet automatisch als Sonderwunsch beim zugewiesenen Concierge.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => { setEditTarget(null); setFormOpen(true); }}
            data-testid="recurring-task-new-btn"
            className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-colors"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.6} />
            Neue Aufgabe
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-navy-dark/60">
          <Loader2 className="w-6 h-6 animate-spin inline-block" strokeWidth={1.6} />
        </div>
      ) : tasks.length === 0 ? (
        <div
          className="bg-white border border-dashed border-navy/20 p-10 text-center"
          data-testid="recurring-tasks-empty"
        >
          <Repeat className="w-8 h-8 mx-auto mb-3 text-navy-dark/40" strokeWidth={1.4} />
          <div className="text-[15px] text-navy leading-snug mb-1">Noch keine wiederkehrenden Aufgaben.</div>
          <div className="text-[12.5px] text-navy-dark/60 italic">
            {isAdmin
              ? "Legen Sie eine an \u2014 z. B. \u201ePoolreinigung woechentlich\u201c."
              : "Der Admin hat noch keine wiederkehrende Aufgabe fuer Sie definiert."}
          </div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="recurring-tasks-list">
          {tasks.map((task) => (
            <li
              key={task.id}
              className="bg-white border border-navy/10 p-5 flex flex-col gap-3 hover:border-gold transition-colors cursor-pointer"
              data-testid={`recurring-task-row-${task.id}`}
              style={{ opacity: task.active ? 1 : 0.6 }}
              onClick={(e) => {
                // Feb 2026 · 17 Sep 2026 · Bugfix „Detailansicht öffnen":
                // Ganze Karte klickbar → Read-Only-Detail-Modal.
                // Klicks auf Action-Buttons (Pause/Spawn/Edit/Delete) sowie
                // die Handles innerhalb der Buttons stoppen die Propagation
                // per `stopPropagation()` unten — der Karten-Klick greift
                // NUR, wenn außerhalb aller Buttons geklickt wurde.
                setDetailTarget(task);
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5"
                      style={{
                        background: task.active ? withAlpha(BRAND_COLORS.gold, 0.15) : withAlpha(BRAND_COLORS.navy, 0.08),
                        color: task.active ? BRAND_COLORS.goldDark : BRAND_COLORS.navy,
                      }}
                    >
                      {FREQUENCY_LABEL[task.frequency] || task.frequency}
                    </span>
                    {!task.active && (
                      <span
                        className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5"
                        style={{ background: withAlpha(STATUS_COLORS.grey, 0.5), color: BRAND_COLORS.navyDark }}
                      >
                        Pausiert
                      </span>
                    )}
                  </div>
                  <div className="font-display text-lg text-navy mt-1 leading-tight" data-testid={`recurring-task-title-${task.id}`}>
                    {task.title}
                  </div>
                  {task.description && (
                    <div className="text-[12.5px] text-navy-dark/70 italic mt-1 leading-snug">
                      {task.description}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleActive(task); }}
                      disabled={busyId === task.id}
                      title={task.active ? "Pausieren" : "Aktivieren"}
                      data-testid={`recurring-task-toggle-${task.id}`}
                      className="p-1.5 text-navy-dark/60 hover:text-navy hover:bg-navy/5 transition-colors"
                    >
                      {task.active ? <PauseCircle className="w-4 h-4" strokeWidth={1.6} /> : <PlayCircle className="w-4 h-4" strokeWidth={1.6} />}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); spawnNow(task); }}
                      disabled={busyId === task.id || !task.active}
                      title="Jetzt feuern"
                      data-testid={`recurring-task-spawn-${task.id}`}
                      className="p-1.5 text-navy-dark/60 hover:text-navy hover:bg-navy/5 transition-colors disabled:opacity-30"
                    >
                      <Play className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setEditTarget(task); setFormOpen(true); }}
                      title="Bearbeiten"
                      data-testid={`recurring-task-edit-${task.id}`}
                      className="p-1.5 text-navy-dark/60 hover:text-navy hover:bg-navy/5 transition-colors"
                    >
                      <ClipboardCheck className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeTask(task); }}
                      disabled={busyId === task.id}
                      title="Loeschen"
                      data-testid={`recurring-task-delete-${task.id}`}
                      className="p-1.5 text-navy-dark/60 hover:text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] pt-2 border-t border-navy/5">
                <div>
                  <div className="text-navy-dark/50 uppercase text-[10px] tracking-widest">Objekt</div>
                  <div className="text-navy" data-testid={`recurring-task-property-${task.id}`}>{propNameById[task.property_id] || "—"}</div>
                </div>
                <div>
                  <div className="text-navy-dark/50 uppercase text-[10px] tracking-widest">Concierge</div>
                  <div className="text-navy" data-testid={`recurring-task-assignee-${task.id}`}>
                    {(() => {
                      // Feb 2026 · 17 Sep 2026 · Bugfix „?"-Anzeige:
                      // Wir nutzen jetzt `assigned_to_name` direkt vom
                      // Server (siehe list_recurring_tasks). Vorteil:
                      // funktioniert für Concierges, die `/team/members`
                      // nicht abrufen dürfen. Legacy-Fallback über die
                      // `memberNameById`-Map bleibt für Admin-Sicht als
                      // Sicherheitsnetz erhalten.
                      const name = task.assigned_to_name
                        || (task.assigned_to ? memberNameById[task.assigned_to] : "")
                        || (() => {
                          const prop = propertyById[task.property_id];
                          const propConc = prop?.assigned_concierge_id;
                          return propConc ? memberNameById[propConc] : "";
                        })();
                      if (!name) {
                        return <span className="text-navy-dark/50 italic">Nicht zugeteilt</span>;
                      }
                      if (task.assigned_via_property) {
                        return (
                          <span data-testid={`recurring-task-assignee-via-property-${task.id}`}>
                            {name}
                            <span className="ml-1.5 text-[10px] tracking-widest uppercase text-navy-dark/45">
                              via Objekt
                            </span>
                          </span>
                        );
                      }
                      return <span>{name}</span>;
                    })()}
                  </div>
                </div>
                <div>
                  <div className="text-navy-dark/50 uppercase text-[10px] tracking-widest">Naechster Termin</div>
                  <div className="text-navy tabular-nums" data-testid={`recurring-task-next-${task.id}`}>{fmtDate(task.next_run_at)}</div>
                </div>
                <div>
                  <div className="text-navy-dark/50 uppercase text-[10px] tracking-widest">Zuletzt gefeuert</div>
                  <div className="text-navy tabular-nums">{task.last_run_at ? fmtDate(task.last_run_at.slice(0, 10)) : <span className="text-navy-dark/50 italic">nie</span>}</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {formOpen && (
        <RecurringTaskForm
          initial={editTarget}
          properties={properties}
          members={members}
          onClose={() => { setFormOpen(false); setEditTarget(null); }}
          onSaved={(saved, mode) => {
            setTasks((cur) => {
              if (mode === "create") return [saved, ...cur];
              return cur.map((t) => (t.id === saved.id ? saved : t));
            });
            setFormOpen(false);
            setEditTarget(null);
          }}
        />
      )}

      {detailTarget && (
        <RecurringTaskDetailModal
          task={detailTarget}
          properties={properties}
          memberNameById={memberNameById}
          propertyById={propertyById}
          isAdmin={isAdmin}
          onClose={() => setDetailTarget(null)}
          onEdit={isAdmin ? () => { setEditTarget(detailTarget); setDetailTarget(null); setFormOpen(true); } : null}
        />
      )}
    </div>
  );
}

// ---------- Read-Only-Detail-Modal (Feb 2026 · 17 Sep 2026) ----------
// User-Directive: Klick auf Aufgabe muss die Details inkl. Wochentage
// anzeigen. Concierges haben kein Edit-Modal; Admins bekommen aus dem
// Detail-Modal einen „Bearbeiten"-Button. Truth-Source für Anzeige-
// Namen bleibt Backend (`assigned_to_name`, `property_name`).
function RecurringTaskDetailModal({ task, properties, memberNameById, propertyById, isAdmin, onClose, onEdit }) {
  const propName = task.property_name
    || propertyById[task.property_id]?.name
    || propertyById[task.property_id]?.address
    || "—";
  const propAddress = propertyById[task.property_id]?.address || "";
  const assigneeName = task.assigned_to_name
    || (task.assigned_to ? memberNameById[task.assigned_to] : "")
    || "";
  const catLabel = CATEGORIES.find((c) => c.v === task.category)?.label || task.category;
  const prioLabel = PRIORITIES.find((p) => p.v === task.priority)?.label || task.priority;
  const freqLabel = FREQUENCY_LABEL[task.frequency] || task.frequency;
  const weekdays = Array.isArray(task.weekdays) && task.weekdays.length > 0
    ? task.weekdays
    : (task.weekday !== undefined && task.weekday !== null ? [task.weekday] : []);
  const daysOfMonth = Array.isArray(task.days_of_month) && task.days_of_month.length > 0
    ? task.days_of_month
    : (task.day_of_month !== undefined && task.day_of_month !== null ? [task.day_of_month] : []);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={onClose}
      data-testid="recurring-task-detail-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="recurring-task-detail-modal"
        data-task-id={task.id}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10">
          <div className="flex items-center gap-2 min-w-0">
            <Repeat className="w-5 h-5 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            <h2 className="font-display text-2xl text-navy truncate" data-testid="recurring-task-detail-title">
              {task.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="recurring-task-detail-close"
            className="p-1 text-navy-dark/60 hover:text-navy"
          >
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5"
              style={{
                background: task.active ? withAlpha(BRAND_COLORS.gold, 0.15) : withAlpha(BRAND_COLORS.navy, 0.08),
                color: task.active ? BRAND_COLORS.goldDark : BRAND_COLORS.navy,
              }}
              data-testid="recurring-task-detail-frequency"
            >
              {freqLabel}
            </span>
            {!task.active && (
              <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5" style={{ background: withAlpha(STATUS_COLORS.grey, 0.5), color: BRAND_COLORS.navyDark }}>
                Pausiert
              </span>
            )}
            <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border border-navy/15 text-navy-dark/70">
              {catLabel}
            </span>
            <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border border-navy/15 text-navy-dark/70">
              Prio: {prioLabel}
            </span>
          </div>

          {task.description && (
            <div className="text-[13.5px] text-navy-dark leading-relaxed italic border-l-2 border-gold pl-3">
              {task.description}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13px]">
            <div>
              <div className="label-caps mb-1 flex items-center gap-1"><MapPin size={11} strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} /> Objekt</div>
              <div className="text-navy" data-testid="recurring-task-detail-property">{propName}</div>
              {propAddress && propAddress !== propName && (
                <div className="text-[11.5px] text-navy-dark/55 mt-0.5 truncate">{propAddress}</div>
              )}
            </div>
            <div>
              <div className="label-caps mb-1 flex items-center gap-1"><UserCheck size={11} strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} /> Concierge</div>
              <div className="text-navy" data-testid="recurring-task-detail-assignee">
                {assigneeName || <span className="text-navy-dark/50 italic">Nicht zugeteilt</span>}
              </div>
              {task.assigned_via_property && assigneeName && (
                <div className="text-[10px] tracking-widest uppercase text-navy-dark/45 mt-0.5">via Objekt</div>
              )}
            </div>
            <div>
              <div className="label-caps mb-1 flex items-center gap-1"><CalendarClock size={11} strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} /> Nächster Termin</div>
              <div className="text-navy tabular-nums" data-testid="recurring-task-detail-next">{fmtDate(task.next_run_at)}</div>
            </div>
            <div>
              <div className="label-caps mb-1 flex items-center gap-1"><Info size={11} strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} /> Zuletzt gefeuert</div>
              <div className="text-navy tabular-nums" data-testid="recurring-task-detail-last">
                {task.last_run_at ? fmtDate(task.last_run_at.slice(0, 10)) : <span className="text-navy-dark/50 italic">nie</span>}
              </div>
            </div>
          </div>

          {(task.frequency === "weekly" || task.frequency === "biweekly") && (
            <div>
              <div className="label-caps mb-2">Wochentage</div>
              <div className="flex gap-1.5 flex-wrap" data-testid="recurring-task-detail-weekdays">
                {WEEKDAYS.map((d) => {
                  const active = weekdays.map(Number).includes(d.v);
                  return (
                    <span
                      key={d.v}
                      data-testid={`recurring-task-detail-weekday-${d.v}`}
                      data-active={active ? "true" : "false"}
                      className={`w-10 h-10 flex items-center justify-center border text-[12px] tracking-wider uppercase ${
                        active
                          ? "border-gold bg-gold text-navy"
                          : "border-navy/15 text-navy-dark/35"
                      }`}
                    >
                      {d.label}
                    </span>
                  );
                })}
              </div>
              {weekdays.length === 0 && (
                <div className="mt-1.5 text-[11px] text-navy-dark/55 italic">Keine Wochentage hinterlegt.</div>
              )}
            </div>
          )}

          {task.frequency === "monthly" && (
            <div>
              <div className="label-caps mb-2">Tage im Monat</div>
              <div className="grid grid-cols-7 sm:grid-cols-10 gap-1.5" data-testid="recurring-task-detail-days-of-month">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                  const active = daysOfMonth.map(Number).includes(d);
                  return (
                    <span
                      key={d}
                      data-testid={`recurring-task-detail-dom-${d}`}
                      data-active={active ? "true" : "false"}
                      className={`h-8 flex items-center justify-center border text-[11.5px] tabular-nums ${
                        active
                          ? "border-gold bg-gold text-navy"
                          : "border-navy/15 text-navy-dark/35"
                      }`}
                    >
                      {d}
                    </span>
                  );
                })}
              </div>
              {daysOfMonth.length === 0 && (
                <div className="mt-1.5 text-[11px] text-navy-dark/55 italic">Keine Tage im Monat hinterlegt.</div>
              )}
              {daysOfMonth.some((d) => Number(d) >= 29) && (
                <div className="mt-1.5 text-[11px] text-navy-dark/55 italic">Tage 29–31 werden bei kürzeren Monaten automatisch auf den letzten Tag geclampt.</div>
              )}
            </div>
          )}

          {task.frequency === "daily" && (
            <div className="text-[12.5px] text-navy-dark/70 italic">
              Diese Aufgabe wird jeden Tag automatisch gefeuert.
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-navy/10 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
            data-testid="recurring-task-detail-cancel"
          >
            Schliessen
          </button>
          {isAdmin && onEdit && (
            <button
              type="button"
              onClick={onEdit}
              data-testid="recurring-task-detail-edit-btn"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90"
              style={{ background: BRAND_COLORS.navy }}
            >
              <ClipboardCheck className="w-3.5 h-3.5" strokeWidth={1.6} />
              Bearbeiten
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Formular als Portal-Dialog ----------
function RecurringTaskForm({ initial, properties, members, onClose, onSaved }) {
  const isEdit = !!initial;
  // Feb 2026 · 16 Sep 2026 · User-Directive „Objekt-Concierge übernehmen":
  // Beim Öffnen des Modals belegen wir `assigned_to` automatisch mit dem
  // Concierge des gewählten Objekts. Beim Wechsel des Objekts synct sich
  // die Auswahl (siehe useEffect unten) — außer der Nutzer hat manuell
  // eine andere Person gewählt (`assigneeTouched=true`).
  const propertyById = useMemo(
    () => Object.fromEntries((properties || []).map((p) => [p.id, p])),
    [properties],
  );
  const initialPropertyId = initial?.property_id || properties[0]?.id || "";
  const initialAssignedTo = (
    initial?.assigned_to
    || propertyById[initialPropertyId]?.assigned_concierge_id
    || ""
  );
  // Feb 2026 · 16 Sep 2026 · Multi-Day-Support: `weekdays` + `days_of_month`
  // sind jetzt Arrays. Beim Editieren einer Legacy-Task fallen wir auf
  // `[weekday]` / `[day_of_month]` zurück.
  const initialWeekdays = Array.isArray(initial?.weekdays) && initial.weekdays.length > 0
    ? initial.weekdays
    : (initial?.weekday !== undefined && initial?.weekday !== null ? [Number(initial.weekday)] : [1]);
  const initialDaysOfMonth = Array.isArray(initial?.days_of_month) && initial.days_of_month.length > 0
    ? initial.days_of_month
    : (initial?.day_of_month !== undefined && initial?.day_of_month !== null ? [Number(initial.day_of_month)] : [1]);
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    property_id: initialPropertyId,
    assigned_to: initialAssignedTo,
    category: initial?.category || "home_care",
    priority: initial?.priority || "normal",
    frequency: initial?.frequency || "weekly",
    weekdays: initialWeekdays,
    days_of_month: initialDaysOfMonth,
    next_run_at: initial?.next_run_at || "",
    active: initial?.active ?? true,
  });
  const [assigneeTouched, setAssigneeTouched] = useState(!!initial?.assigned_to);
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Wenn das Objekt gewechselt wird, ziehen wir den Objekt-Concierge
  // automatisch als Standard-Assignee — bis der Nutzer explizit einen
  // anderen wählt.
  useEffect(() => {
    if (assigneeTouched) return;
    const propConc = propertyById[form.property_id]?.assigned_concierge_id || "";
    setForm((f) => (f.assigned_to === propConc ? f : { ...f, assigned_to: propConc }));
  }, [form.property_id, propertyById, assigneeTouched]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Titel erforderlich."); return; }
    if (!form.property_id) { toast.error("Objekt erforderlich."); return; }
    // Feb 2026 · 16 Sep 2026 · Multi-Day-Validation im Frontend
    if (["weekly", "biweekly"].includes(form.frequency) && (!form.weekdays || form.weekdays.length === 0)) {
      toast.error("Bitte mindestens einen Wochentag auswählen.");
      return;
    }
    if (form.frequency === "monthly" && (!form.days_of_month || form.days_of_month.length === 0)) {
      toast.error("Bitte mindestens einen Tag im Monat auswählen.");
      return;
    }
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      property_id: form.property_id,
      assigned_to: form.assigned_to || null,
      category: form.category,
      priority: form.priority,
      frequency: form.frequency,
      weekdays: ["weekly", "biweekly"].includes(form.frequency)
        ? [...form.weekdays].map(Number).sort((a, b) => a - b)
        : [],
      days_of_month: form.frequency === "monthly"
        ? [...form.days_of_month].map(Number).sort((a, b) => a - b)
        : [],
    };
    if (form.next_run_at) payload.next_run_at = form.next_run_at;
    setBusy(true);
    try {
      let saved;
      if (isEdit) {
        const patchPayload = { ...payload, active: form.active };
        const r = await api.patch(`/team/recurring-tasks/${initial.id}`, patchPayload);
        saved = r.data;
      } else {
        const r = await api.post("/team/recurring-tasks", payload);
        saved = r.data;
      }
      toast.success(isEdit ? "Aufgabe aktualisiert." : "Aufgabe angelegt.");
      onSaved(saved, isEdit ? "update" : "create");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={onClose}
      data-testid="recurring-task-form-overlay"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="recurring-task-form"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10">
          <div className="flex items-center gap-2">
            <Repeat className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            <h2 className="font-display text-2xl text-navy">
              {isEdit ? "Aufgabe bearbeiten" : "Neue wiederkehrende Aufgabe"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-navy-dark/60 hover:text-navy" data-testid="recurring-task-form-close">
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-caps block mb-1.5">Titel</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="z. B. Poolreinigung"
              maxLength={120}
              data-testid="recurring-task-title-input"
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              required
            />
          </div>
          <div>
            <label className="label-caps block mb-1.5">Beschreibung <span className="text-navy-dark/40 italic normal-case">(optional)</span></label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              maxLength={500}
              placeholder="Details fuer den Concierge — was genau ist zu tun?"
              data-testid="recurring-task-description-input"
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy leading-snug"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-caps block mb-1.5">Objekt</label>
              <select
                value={form.property_id}
                onChange={(e) => set({ property_id: e.target.value })}
                data-testid="recurring-task-property-select"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              >
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Concierge <span className="text-navy-dark/40 italic normal-case">(optional)</span></label>
              <select
                value={form.assigned_to}
                onChange={(e) => { set({ assigned_to: e.target.value }); setAssigneeTouched(true); }}
                data-testid="recurring-task-assignee-select"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              >
                <option value="">— Kein Concierge —</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email} ({m.role})</option>)}
              </select>
              {(() => {
                // Feb 2026 · 16 Sep 2026 · Hinweis-Zeile: signalisiert dem
                // Admin klar, dass die Auswahl automatisch aus dem
                // Objekt-Concierge übernommen wurde, und macht Overrides
                // sichtbar.
                const propConc = propertyById[form.property_id]?.assigned_concierge_id;
                const propConcName = propConc ? (members.find((m) => m.id === propConc)?.name || members.find((m) => m.id === propConc)?.email) : null;
                if (!propConc) {
                  return (
                    <div className="mt-1 text-[11px] text-navy-dark/50 italic">
                      Dieses Objekt hat keinen zugewiesenen Concierge.
                    </div>
                  );
                }
                if (form.assigned_to === propConc) {
                  return (
                    <div className="mt-1 text-[11px] italic" style={{ color: BRAND_COLORS.gold }} data-testid="recurring-task-assignee-hint-inherited">
                      Aus Objekt übernommen · {propConcName}
                    </div>
                  );
                }
                return (
                  <div className="mt-1 text-[11px] text-navy-dark/60 italic" data-testid="recurring-task-assignee-hint-override">
                    Objekt-Concierge: {propConcName} · Sie haben abweichend zugewiesen
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="label-caps block mb-1.5">Kategorie</label>
              <select
                value={form.category}
                onChange={(e) => set({ category: e.target.value })}
                data-testid="recurring-task-category-select"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              >
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Prioritaet</label>
              <select
                value={form.priority}
                onChange={(e) => set({ priority: e.target.value })}
                data-testid="recurring-task-priority-select"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              >
                {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="border-t border-navy/10 pt-4">
            <label className="label-caps block mb-1.5" style={{ color: BRAND_COLORS.gold }}>Frequenz</label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {Object.entries(FREQUENCY_LABEL).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => set({ frequency: k })}
                  data-testid={`recurring-task-freq-${k}`}
                  className={`px-3 py-2 text-[11.5px] tracking-wider uppercase border transition-colors ${
                    form.frequency === k
                      ? "border-navy bg-navy text-ivory"
                      : "border-navy/25 text-navy-dark/70 hover:border-navy"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {(form.frequency === "weekly" || form.frequency === "biweekly") && (
              <div>
                <label className="text-[11px] tracking-widest uppercase text-navy-dark/60 block mb-1.5">
                  Wochentage <span className="normal-case italic text-navy-dark/45">(mehrere möglich)</span>
                </label>
                <div className="flex gap-2 flex-wrap">
                  {WEEKDAYS.map((d) => {
                    const active = form.weekdays.map(Number).includes(d.v);
                    return (
                      <button
                        key={d.v}
                        type="button"
                        onClick={() => {
                          const cur = form.weekdays.map(Number);
                          const next = active
                            ? cur.filter((x) => x !== d.v)
                            : [...cur, d.v].sort((a, b) => a - b);
                          set({ weekdays: next });
                        }}
                        data-testid={`recurring-task-weekday-${d.v}`}
                        aria-pressed={active}
                        className={`w-11 h-11 flex items-center justify-center border text-[12px] tracking-wider uppercase transition-colors ${
                          active
                            ? "border-gold bg-gold text-navy"
                            : "border-navy/25 text-navy-dark/70 hover:border-gold"
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 text-[11px] text-navy-dark/55 italic" data-testid="recurring-task-weekdays-count">
                  {form.weekdays.length === 0
                    ? "Kein Wochentag ausgewählt."
                    : `${form.weekdays.length} Wochentag${form.weekdays.length === 1 ? "" : "e"} ausgewählt.`}
                </div>
              </div>
            )}
            {form.frequency === "monthly" && (
              <div>
                <label className="text-[11px] tracking-widest uppercase text-navy-dark/60 block mb-1.5">
                  Tage im Monat <span className="normal-case italic text-navy-dark/45">(mehrere möglich, 1–31)</span>
                </label>
                <div className="grid grid-cols-7 md:grid-cols-10 gap-1.5">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                    const active = form.days_of_month.map(Number).includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          const cur = form.days_of_month.map(Number);
                          const next = active
                            ? cur.filter((x) => x !== d)
                            : [...cur, d].sort((a, b) => a - b);
                          set({ days_of_month: next });
                        }}
                        data-testid={`recurring-task-dom-${d}`}
                        aria-pressed={active}
                        className={`h-9 flex items-center justify-center border text-[11.5px] tabular-nums transition-colors ${
                          active
                            ? "border-gold bg-gold text-navy"
                            : "border-navy/20 text-navy-dark/70 hover:border-gold"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 text-[11px] text-navy-dark/55 italic" data-testid="recurring-task-dom-count">
                  {form.days_of_month.length === 0
                    ? "Kein Tag im Monat ausgewählt."
                    : `${form.days_of_month.length} Tag${form.days_of_month.length === 1 ? "" : "e"} ausgewählt. Tage 29–31 werden bei kürzeren Monaten automatisch auf den letzten Tag geclampt.`}
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="label-caps block mb-1.5">
              Erster Termin <span className="text-navy-dark/40 italic normal-case">(optional — wenn leer wird automatisch berechnet)</span>
            </label>
            <input
              type="date"
              value={form.next_run_at}
              onChange={(e) => set({ next_run_at: e.target.value })}
              data-testid="recurring-task-next-input"
              className="w-full md:w-64 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
            />
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 pt-2 border-t border-navy/10">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set({ active: e.target.checked })}
                data-testid="recurring-task-active-toggle"
              />
              <span className="text-[13px] text-navy">Aktiv (feuert per Cron)</span>
            </label>
          )}
        </div>
        <div className="px-6 py-4 border-t border-navy/10 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
            data-testid="recurring-task-form-cancel"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="recurring-task-form-save-btn"
            className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
            {isEdit ? "Aenderungen speichern" : "Aufgabe anlegen"}
          </button>
        </div>
      </form>
    </div>
  );
}
